import styled, { css } from 'styled-components';
import { flexCenterVertical, flexColumn, flexColumnCenter } from '@stylesheets/helpers';

export const Container = styled.div`
  ${flexColumn}
  width: 100%;

  ${({ theme: { spacing } }) => css`
    padding-bottom: ${spacing?.level6};
  `}

  > h2 {
    text-align: center;
    ${({ theme: { font, colors, spacing } }) => css`
      ${font?.headlineMedium}
      font-weight: 700;
      color: ${colors?.onSurface};
      margin: 0 0 ${spacing?.level3};
    `}
  }

  > p {
    text-align: center;
    ${({ theme: { font, colors, spacing } }) => css`
      ${font?.bodyLarge}
      color: ${colors?.onSurfaceVariant};
      margin: 0 0 ${spacing?.level6};
    `}
  }
`;

export const ToggleRow = styled.div`
  position: relative;
  ${flexCenterVertical}
  justify-content: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level4};
    margin-bottom: ${spacing?.level6};
  `}
`;

export const ToggleLabel = styled.span<{ $active?: boolean }>`
  ${({ theme: { font, colors }, $active }) => css`
    ${font?.labelLarge}
    color: ${$active ? colors?.onSurface : colors?.outline};
    font-weight: ${$active ? 600 : 400};
  `}
`;

export const ToggleSwitch = styled.button<{ $yearly?: boolean }>`
  ${({ theme: { colors, borderRadius }, $yearly }) => css`
    width: 48px;
    height: 26px;
    border-radius: ${borderRadius?.full};
    border: none;
    background: ${$yearly ? colors?.primary : colors?.surfaceVariant};
    position: relative;
    cursor: pointer;
    transition: background 0.2s;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: ${$yearly ? '24px' : '3px'};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: white;
      transition: left 0.2s;
    }
  `}
`;

export const SaveBadge = styled.span<{ $visible?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${({ theme: { font, colors, spacing }, $visible }) => css`
    ${font?.labelMedium}
    color: ${colors?.primary};
    left: calc(50% + 80px + ${spacing?.level4});
    visibility: ${$visible ? 'visible' : 'hidden'};
  `}

  @media (max-width: 480px) {
    display: none;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 30px;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level5};
  `}

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

export const Card = styled.div<{ $highlighted?: boolean }>`
  ${flexColumn}

  ${({ theme: { colors, borderRadius, spacing }, $highlighted }) => css`
    background: ${$highlighted ? colors?.surfaceContainerLow : colors?.surface};
    border: 1px solid ${$highlighted ? colors?.primary : colors?.outlineVariant};
    border-radius: ${borderRadius?.large};
    padding: ${spacing?.level7};
    position: relative;
  `}
`;

export const PopularBadge = styled.div`
  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    padding: ${spacing?.level2} ${spacing?.level5};
    background: ${colors?.primary};
    color: ${colors?.onPrimary};
    border-radius: ${borderRadius?.full};
    ${font?.labelMedium}
    white-space: nowrap;
  `}
`;

export const FreeBadge = styled.div`
  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    padding: ${spacing?.level2} ${spacing?.level5};
    background: ${colors?.secondary ?? colors?.surfaceContainerHigh};
    color: ${colors?.onSecondary ?? colors?.onSurface};
    border-radius: ${borderRadius?.full};
    ${font?.labelMedium}
    white-space: nowrap;
  `}
`;

/** Fills available space above the CTA button so buttons align across all cards. */
export const CardTop = styled.div`
  ${flexColumn}
  flex: 1;
`;

export const PlanName = styled.h3`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.labelLarge}
    text-transform: uppercase;
    letter-spacing: 1px;
    color: ${colors?.onSurface};
    margin: 0 0 ${spacing?.level4};
  `}
`;

export const Price = styled.div`
  ${flexCenterVertical}
`;

export const PriceAmount = styled.span`
  ${({ theme: { font, colors } }) => css`
    ${font?.displaySmall}
    font-weight: 700;
    color: ${colors?.onSurface};
  `}
`;

export const PricePeriod = styled.span`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurfaceVariant};
    margin-left: ${spacing?.level2};
  `}
`;

export const BillingPeriod = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.bodySmall}
    color: ${colors?.outline};
    margin: ${spacing?.level2} 0 ${spacing?.level6};
    min-height: ${font?.bodySmall?.lineHeight};
  `}
`;

export const SectionLabel = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.labelSmall}
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors?.outline};
    margin: 0 0 ${spacing?.level4};
  `}
`;

export const PlanButton = styled.button<{ $primary?: boolean; $loading?: boolean }>`
  ${({ theme: { colors, font, spacing, borderRadius }, $primary, $loading }) => css`
    width: 100%;
    padding: 10px ${spacing?.level6};
    border-radius: ${borderRadius?.full};
    ${font?.labelLarge}
    cursor: pointer;
    margin-bottom: ${spacing?.level6};
    border: none;
    transition: all 0.2s;
    opacity: ${$loading ? 0.7 : 1};

    ${$primary
      ? css`
          background: ${colors?.primary};
          color: ${colors?.onPrimary};

          &:hover:not(:disabled) {
            opacity: 0.88;
          }
        `
      : css`
          background: transparent;
          color: ${colors?.onSurface};
          border: 1px solid ${colors?.outline};

          &:hover:not(:disabled) {
            background: ${colors?.surfaceContainerHigh};
          }
        `}

    &:disabled {
      cursor: not-allowed;
    }
  `}
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

export const Feature = styled.li`
  ${({ theme: { font, colors, spacing } }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${spacing?.level3};
    ${font?.bodySmall}
    color: ${colors?.onSurfaceVariant};
    margin-bottom: ${spacing?.level4};

    &::before {
      content: '\\2713';
      color: ${colors?.primary};
      font-weight: 700;
      flex-shrink: 0;
      margin-top: 1px;
    }

    &:first-child {
      color: ${colors?.onSurface};
      font-weight: 600;
    }
  `}
`;

export const PlanSubtitle = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.bodySmall}
    color: ${colors?.outline};
    margin: 0 0 ${spacing?.level5};
  `}
`;

export const VolumeSelector = styled.select`
  ${({ theme: { font, colors, spacing, borderRadius } }) => css`
    width: 100%;
    padding: ${spacing?.level3} ${spacing?.level4};
    border-radius: ${borderRadius?.small};
    border: 1px solid ${colors?.outlineVariant};
    background: ${colors?.surfaceContainerHigh};
    color: ${colors?.onSurface};
    ${font?.bodyMedium}
    font-family: inherit;
    cursor: pointer;
    margin-bottom: ${spacing?.level5};
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%23999' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right ${spacing?.level4} center;
    padding-right: ${spacing?.level8};

    &:focus {
      outline: none;
      border-color: ${colors?.primary};
    }
  `}
`;

export const LoadingWrapper = styled.div`
  ${flexColumnCenter}
  min-height: 200px;

  ${({ theme: { font, colors } }) => css`
    color: ${colors?.onSurfaceVariant};
    ${font?.bodyLarge}
  `}
`;

export const ErrorText = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    text-align: center;
    color: ${colors?.error};
    ${font?.bodyMedium}
    margin-top: ${spacing?.level5};
  `}
`;
